'use client';

import { c as _c } from "react/compiler-runtime";
import type { InterfaceInputToken, Point, Rewards } from '@morpho-org/dapp-core';
import { Skeleton } from '@morpho-org/ui-v6/server';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import { formatApyPercentage, formatWadApyPercentage } from '@/helpers/formatters';
import type { VaultV2ApyData } from '../../helpers/vaultV2Apy';
import { VaultV2RowListRow, VaultV2RowListSection } from '../../components/VaultV2RowList';
interface VaultV2PerformanceReturnsProps {
  instantApy?: bigint | null;
  apy?: bigint | null;
  performanceFee?: bigint | null;
  managementFee?: bigint | null;
  maxRate?: bigint | null;
  apyBreakdown?: VaultV2ApyData['apyBreakdown'];
  maxApy?: number | null;
  isRateCapped?: boolean;
  includesDonations?: boolean;
  rewards?: Rewards[];
  points?: Point[];
  asset?: InterfaceInputToken | null;
  apy7d?: number | null;
  apy30d?: number | null;
  apy90d?: number | null;
  isLoading?: boolean;
}
const VaultV2PerformanceReturns = t0 => {
  const $ = _c(41);
  const {
    instantApy,
    apy,
    performanceFee,
    managementFee,
    maxRate,
    apyBreakdown,
    maxApy,
    isRateCapped,
    includesDonations,
    rewards: t1,
    points: t2,
    asset,
    apy7d,
    apy30d,
    apy90d,
    isLoading
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const rewards = t3;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const points = t4;
  let t5;
  if ($[4] !== instantApy) {
    t5 = formatWadApyPercentage(instantApy);
    $[4] = instantApy;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const formattedInstantValue = t5;
  let t6;
  if ($[6] !== apy7d) {
    t6 = formatApyPercentage(apy7d);
    $[6] = apy7d;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const formatted7dValue = t6;
  let t7;
  if ($[8] !== apy30d) {
    t7 = formatApyPercentage(apy30d);
    $[8] = apy30d;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const formatted30dValue = t7;
  let t8;
  if ($[10] !== apy90d) {
    t8 = formatApyPercentage(apy90d);
    $[10] = apy90d;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const formatted90dValue = t8;
  let t9;
  if ($[12] !== apy || $[13] !== apyBreakdown || $[14] !== asset || $[15] !== formattedInstantValue || $[16] !== includesDonations || $[17] !== instantApy || $[18] !== isLoading || $[19] !== isRateCapped || $[20] !== managementFee || $[21] !== maxApy || $[22] !== maxRate || $[23] !== performanceFee || $[24] !== points || $[25] !== rewards) {
    t9 = <VaultV2RowListRow label="Instant APY" tooltip="Instant vault net APY, including fees and rewards.">{isLoading ? <Skeleton className="h-md w-[60px]" /> : <IconRewardHover RewardsHoverCardProps={{
        apy,
        rewards,
        points,
        variant: "vault",
        netApy: instantApy,
        asset,
        durationText: "Instant",
        performanceFee,
        managementFee,
        maxRate,
        apyBreakdown,
        maxApy,
        isRateCapped,
        includesDonations
      }}><div className="flex"><span className="text-foreground text-xs">{formattedInstantValue}</span></div></IconRewardHover>}</VaultV2RowListRow>;
    $[12] = apy;
    $[13] = apyBreakdown;
    $[14] = asset;
    $[15] = formattedInstantValue;
    $[16] = includesDonations;
    $[17] = instantApy;
    $[18] = isLoading;
    $[19] = isRateCapped;
    $[20] = managementFee;
    $[21] = maxApy;
    $[22] = maxRate;
    $[23] = performanceFee;
    $[24] = points;
    $[25] = rewards;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== formatted7dValue || $[28] !== isLoading) {
    t10 = <VaultV2RowListRow label="7D APY" tooltip="Weekly vault net APY, including fees but excluding rewards.">{isLoading ? <Skeleton className="h-md w-[60px]" /> : <span className="text-foreground text-xs">{formatted7dValue}</span>}</VaultV2RowListRow>;
    $[27] = formatted7dValue;
    $[28] = isLoading;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== formatted30dValue || $[31] !== isLoading) {
    t11 = <VaultV2RowListRow label="30D APY" tooltip="Monthly vault net APY, including fees but excluding rewards.">{isLoading ? <Skeleton className="h-md w-[60px]" /> : <span className="text-foreground text-xs">{formatted30dValue}</span>}</VaultV2RowListRow>;
    $[30] = formatted30dValue;
    $[31] = isLoading;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== formatted90dValue || $[34] !== isLoading) {
    t12 = <VaultV2RowListRow label="90D APY" tooltip="90D vault net APY, including fees but excluding rewards.">{isLoading ? <Skeleton className="h-md w-[60px]" /> : <span className="text-foreground text-xs">{formatted90dValue}</span>}</VaultV2RowListRow>;
    $[33] = formatted90dValue;
    $[34] = isLoading;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t9) {
    t13 = <VaultV2RowListSection title="Returns">{t9}{t10}{t11}{t12}</VaultV2RowListSection>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t9;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  return t13;
};
export default VaultV2PerformanceReturns;